import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Tag, Percent, ListTree, Star } from "lucide-react";

const emptyItem = () => ({ label: "", value: "", options: [] });

function OptionsEditor({ options, onChange }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (!t) return;
    onChange([...(options || []), t]);
    setText("");
  };
  return (
    <div className="mt-1.5">
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {(options || []).map((o, i) => (
          <span key={`${o}-${i}`} data-testid={`opt-chip-${i}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary border border-border">
            {o}
            <button type="button" onClick={() => onChange(options.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive">×</button>
          </span>
        ))}
        {(options || []).length === 0 && <span className="text-[11px] text-muted-foreground">No dropdown options (leave blank for a simple label / value row)</span>}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Add dropdown option and press Enter" className="rounded-xl h-8 text-xs" />
        <Button type="button" size="sm" variant="outline" onClick={add} className="rounded-full h-8"><Plus className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
}

function PlanEditor({ plan, onSaved }) {
  const [form, setForm] = useState(plan);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(plan), [plan]);

  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const setFeature = (i, v) => setForm((f) => ({ ...f, features: f.features.map((x, idx) => (idx === i ? v : x)) }));
  const addFeature = () => setForm((f) => ({ ...f, features: [...(f.features || []), ""] }));
  const rmFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  const setItem = (i, key, val) => setForm((f) => ({ ...f, samagri: f.samagri.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)) }));
  const addItem = () => setForm((f) => ({ ...f, samagri: [...(f.samagri || []), emptyItem()] }));
  const rmItem = (i) => setForm((f) => ({ ...f, samagri: f.samagri.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        price_add: Number(form.price_add) || 0,
        popular_tag: form.popular_tag || "",
        discount_tag: form.discount_tag || "",
        features: (form.features || []).filter((f) => (f || "").trim()),
        samagri: (form.samagri || []).filter((it) => (it.label || "").trim()),
        sort_order: Number(form.sort_order) || 1,
      };
      const r = await api.put(`/admin/plans/${plan.id}`, payload);
      toast.success(`${form.name} plan updated`);
      const updated = r.data.find((p) => p.id === plan.id);
      if (updated) onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`plan-editor-${plan.id}`} className="rounded-2xl border border-border bg-card p-5 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Star className="h-5 w-5 text-primary" />
          <h3 className="font-serif text-xl font-semibold capitalize">{plan.id} plan</h3>
        </div>
        <Button onClick={save} disabled={saving} data-testid={`save-plan-${plan.id}`} className="rounded-full bg-primary text-primary-foreground">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="mb-1.5 block text-xs">Plan name</Label>
          <Input data-testid={`plan-name-${plan.id}`} value={form.name || ""} onChange={(e) => patch({ name: e.target.value })} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Price add-on (₹)</Label>
          <Input type="number" min="0" data-testid={`plan-price-${plan.id}`} value={form.price_add ?? 0} onChange={(e) => patch({ price_add: e.target.value })} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1.5 flex items-center gap-1 text-xs"><Star className="h-3 w-3" /> Popular tag (leave blank to hide)</Label>
          <Input data-testid={`plan-popular-${plan.id}`} value={form.popular_tag || ""} onChange={(e) => patch({ popular_tag: e.target.value })} placeholder="e.g. Most Popular" className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1.5 flex items-center gap-1 text-xs"><Percent className="h-3 w-3" /> Discount tag (leave blank to hide)</Label>
          <Input data-testid={`plan-discount-${plan.id}`} value={form.discount_tag || ""} onChange={(e) => patch({ discount_tag: e.target.value })} placeholder="e.g. Save ₹200" className="rounded-xl" />
        </div>
      </div>

      <div>
        <Label className="mb-1.5 block text-xs">Description</Label>
        <Textarea data-testid={`plan-desc-${plan.id}`} value={form.description || ""} onChange={(e) => patch({ description: e.target.value })} rows={3} className="rounded-xl" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs flex items-center gap-1"><Tag className="h-3 w-3" /> Features</Label>
          <Button type="button" size="sm" variant="outline" onClick={addFeature} data-testid={`add-feature-${plan.id}`} className="rounded-full h-8"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
        </div>
        <div className="space-y-2">
          {(form.features || []).map((f, i) => (
            <div key={`feat-${i}`} className="flex gap-2">
              <Input data-testid={`plan-feature-${plan.id}-${i}`} value={f} onChange={(e) => setFeature(i, e.target.value)} className="rounded-xl" />
              <Button type="button" size="sm" variant="ghost" onClick={() => rmFeature(i)} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {(form.features || []).length === 0 && <p className="text-xs text-muted-foreground">No features yet.</p>}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs flex items-center gap-1"><ListTree className="h-3 w-3" /> Puja samagri &amp; details</Label>
          <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid={`add-item-${plan.id}`} className="rounded-full h-8"><Plus className="h-3.5 w-3.5 mr-1" /> Add item</Button>
        </div>
        <div className="space-y-3">
          {(form.samagri || []).map((it, i) => (
            <div key={`item-${i}`} className="rounded-xl border border-border p-3" data-testid={`plan-item-${plan.id}-${i}`}>
              <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <Label className="mb-1 block text-[11px] uppercase tracking-wide">Label</Label>
                  <Input value={it.label || ""} data-testid={`item-label-${plan.id}-${i}`} onChange={(e) => setItem(i, "label", e.target.value)} placeholder="e.g. Flowers" className="rounded-xl h-9" />
                </div>
                <div>
                  <Label className="mb-1 block text-[11px] uppercase tracking-wide">Value / quantity</Label>
                  <Input value={it.value || ""} data-testid={`item-value-${plan.id}-${i}`} onChange={(e) => setItem(i, "value", e.target.value)} placeholder="e.g. 200 gram" className="rounded-xl h-9" />
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={() => rmItem(i)} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="mt-2">
                <Label className="text-[11px] uppercase tracking-wide">Dropdown options (optional)</Label>
                <OptionsEditor options={it.options || []} onChange={(opts) => setItem(i, "options", opts)} />
              </div>
            </div>
          ))}
          {(form.samagri || []).length === 0 && <p className="text-xs text-muted-foreground">No items yet.</p>}
        </div>
      </div>

      <div className="pt-3 border-t border-border">
        <p className="text-xs text-muted-foreground">Preview price shown in booking flow: <span className="font-semibold text-primary">{Number(form.price_add) > 0 ? `+ ${inr(Number(form.price_add))}` : "Included"}</span></p>
      </div>
    </div>
  );
}

export default function AdminPlansCMS() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/plans").then((r) => setPlans(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onSaved = (updated) => setPlans((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));

  if (loading) return <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  return (
    <div className="space-y-5" data-testid="admin-plans-cms">
      <p className="text-sm text-muted-foreground">Edit each booking plan below — name, description, price, tags, features, and puja samagri details. Changes go live instantly on the booking flow.</p>
      <div className="grid lg:grid-cols-2 gap-5">
        {plans.map((p) => (<PlanEditor key={p.id} plan={p} onSaved={onSaved} />))}
      </div>
    </div>
  );
}
